// Embed bridge for the iframe host (cortex-website). Greenfield postMessage
// seam: on /embed we hide the standalone chrome, announce 'ready' to the parent,
// accept a template (tagged with a token) over a channel- and origin-verified
// message, ack that token's render outcome ('rendered' {token, ok}), and report
// our content height up so the parent can size the frame. Imports nothing from
// vendor/ so the webpack prune closure is unchanged. Trusted-origin model: the
// template is the parent's authenticated backend output; we route it straight
// into the existing seed path (no sanitizer, no re-render).

export const CFP_CHANNEL = 'cortex-form-preview';

// Embed mode is a client-side branch: static-server.js SPA-falls-back /embed to
// the same index.html, so the app decides, not the server.
export function isEmbedMode() {
  return typeof location !== 'undefined' && location.pathname.indexOf('/embed') === 0;
}

// Wire the bridge. `onData(template)` seeds+renders (App passes onTemplate).
// Returns a disposer (removes the listener + observer). Fails closed when the
// parentOrigin query param is absent/malformed.
export function initEmbedBridge({ onData }) {
  if (typeof window === 'undefined') return function () {};
  var parentOrigin = new URLSearchParams(location.search).get('parentOrigin') || '';
  if (!/^https?:\/\/[^/]+$/.test(parentOrigin)) return function () {}; // fail closed

  document.documentElement.classList.add('cfp-embed'); // content-driven height (see styles.css)

  function post(msg) {
    try {
      if (window.parent) window.parent.postMessage(msg, parentOrigin);
    } catch (e) { /* cross-origin post can throw on a torn-down parent — ignore */ }
  }

  function onMessage(e) {
    if (e.origin !== parentOrigin) return;
    var d = e.data;
    if (!d || d.channel !== CFP_CHANNEL) return;
    if (d.type === 'data') {
      // Ack the render outcome for THIS token so the parent can gate its overlay
      // on actual rendering, not just the one-time 'ready'. A malformed template
      // rejects from the seed path; without this ack the parent would lift the
      // overlay and leave the PRIOR template visible after a failed regeneration.
      var token = d.token;
      Promise.resolve(onData(d.data)).then(
        function () { post({ channel: CFP_CHANNEL, type: 'rendered', token: token, ok: true }); },
        function () { post({ channel: CFP_CHANNEL, type: 'rendered', token: token, ok: false }); }
      );
    }
  }
  window.addEventListener('message', onMessage); // attach BEFORE posting ready

  var raf = 0;
  function report() {
    raf = 0;
    var h = Math.ceil(document.documentElement.getBoundingClientRect().height);
    post({ channel: CFP_CHANNEL, type: 'height', value: h });
  }
  var ro = new ResizeObserver(function () {
    if (!raf) raf = requestAnimationFrame(report); // coalesce bursts to one post/frame
  });
  ro.observe(document.documentElement);
  report(); // initial height

  post({ channel: CFP_CHANNEL, type: 'ready' }); // AFTER the listener is live

  return function dispose() {
    window.removeEventListener('message', onMessage);
    ro.disconnect();
    if (raf) cancelAnimationFrame(raf);
  };
}
